import React, { useCallback, useEffect, useId } from "react";
import { Dismiss20Regular } from "@fluentui/react-icons";
import { Button, type ButtonProps } from "../ui/button";
import { ButtonPending } from "../loading";
import { useIsMobile } from "../../hooks/useIsMobile";
import { cn } from "../../lib/utils";
import styles from "./Modal.module.css";

export type ModalAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  pending?: boolean;
  pendingText?: string;
  variant?: ButtonProps["variant"];
};

export type ModalProps = {
  isOpen: boolean;
  /**
   * Optional so a caller that omits it (CCDS-05 safe-failure) still renders: the
   * close affordance (Escape, backdrop, close button) becomes a no-op and a
   * development-mode warning is logged, rather than throwing (spec.md UAT-4).
   */
  onClose?: () => void;
  title: string;
  children: React.ReactNode;
  primaryAction?: ModalAction;
  secondaryAction?: ModalAction;
  closeAriaLabel?: string;
};

/**
 * Canonical confirm/detail overlay (Cortex Console Design System & Consistency
 * Pass, CCDS-04). Promoted from code/frontend's ConfirmationModal.tsx shape -
 * same onClose contract on backdrop click, plus Escape-to-close, and the same
 * useIsMobile desktop/mobile split (full-screen sheet vs centered dialog).
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  primaryAction,
  secondaryAction,
  closeAriaLabel = "Close",
}: ModalProps) {
  const titleId = useId();
  const isMobile = useIsMobile();

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen || !import.meta.env.DEV) {
      return;
    }
    if (!onClose) {
      console.warn(
        "Modal: rendered without an onClose handler - the close affordance (Escape, backdrop, close button) is a no-op.",
      );
    }
    if (!primaryAction) {
      console.warn(
        "Modal: rendered without a primaryAction (confirm) handler - only the no-op close affordance is available.",
      );
    }
  }, [isOpen, onClose, primaryAction]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={cn(styles.root, !isMobile && styles.rootDesktop)}>
      {!isMobile && (
        <div className={styles.backdrop} onClick={handleClose} aria-hidden="true" />
      )}
      <div
        className={cn(styles.surface, isMobile ? styles.surfaceMobile : styles.surfaceDesktop)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="app-modal"
      >
        <div className={styles.header}>
          <h3 id={titleId} className={styles.title}>
            {title}
          </h3>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label={closeAriaLabel}
          >
            <Dismiss20Regular />
          </button>
        </div>
        <div className={styles.body}>{children}</div>
        <div className={styles.footer}>
          {primaryAction && (
            <Button
              variant={primaryAction.variant}
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
            >
              <ButtonPending
                pending={Boolean(primaryAction.pending)}
                pendingText={primaryAction.pendingText ?? `${primaryAction.label}…`}
              >
                {primaryAction.label}
              </ButtonPending>
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant="outline"
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.disabled}
            >
              {secondaryAction.label}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
